import * as Sentry from '@sentry/sveltekit';
import { browser } from '$app/environment';
import {
	isAuthenticated,
	getTokenRemainingTime,
	shouldRefreshToken
} from '$lib/state/auth.state.svelte';
import { devLog } from './lib/utils/dev';
import type { HandleClientError } from '@sveltejs/kit';
import { installConsoleBuffer } from '$lib/services/monitoring/consoleBuffer';
import { record as recordApi } from '$lib/services/monitoring/apiHistory';

// Install console ring buffer (idempotent — safe across HMR reloads)
installConsoleBuffer({ maxEntries: 100 });

// Install window.fetch shim as a backstop for non-apiClient callsites.
// apiClient sets X-AMOS-Request-Id on every request — the shim detects that
// header and skips recording (apiClient already records it with richer context).
const FETCH_SHIM_INSTALLED = Symbol.for('amos.fetch.shim.installed');
const globalAny = globalThis as Record<symbol, unknown> & {
	fetch: typeof fetch;
};

if (!globalAny[FETCH_SHIM_INSTALLED]) {
	globalAny[FETCH_SHIM_INSTALLED] = true;
	const originalFetch = globalAny.fetch.bind(globalAny);

	globalAny.fetch = async (
		input: RequestInfo | URL,
		init?: RequestInit
	): Promise<Response> => {
		const url =
			typeof input === 'string'
				? input
				: input instanceof URL
					? input.href
					: input.url;

		// Detect apiClient-origin requests via the trace header it sets
		let hasTraceHeader = false;
		try {
			hasTraceHeader = init?.headers
				? new Headers(init.headers).has('X-AMOS-Request-Id')
				: false;
		} catch {
			hasTraceHeader = false;
		}
		if (hasTraceHeader) {
			// apiClient owns this — pass straight through, no recording
			return originalFetch(input, init);
		}

		const method = (init?.method ?? 'GET').toUpperCase();
		const start = performance.now();
		try {
			const response = await originalFetch(input, init);
			recordApi({
				method,
				endpoint: url,
				status: response.status,
				durationMs: performance.now() - start
			});
			return response;
		} catch (err) {
			recordApi({
				method,
				endpoint: url,
				status: 0,
				durationMs: performance.now() - start,
				error: {
					code: (err as Error)?.name || 'Error',
					message: (err as Error)?.message || 'unknown'
				}
			});
			throw err;
		}
	};
}

/**
 * Sentry Initialization
 * Initialize Sentry on the client-side for error tracking and performance monitoring
 */
Sentry.init({
	dsn: import.meta.env.PUBLIC_SENTRY_DSN,

	// Environment detection
	environment: import.meta.env.MODE || 'development',

	// Performance monitoring
	tracesSampleRate: import.meta.env.MODE === 'production' ? 0.1 : 1.0,

	// Session replay for debugging
	replaysSessionSampleRate: import.meta.env.MODE === 'production' ? 0.1 : 1.0,
	replaysOnErrorSampleRate: 1.0,

	// Enable integrations
	integrations: [
		Sentry.browserTracingIntegration(),
		Sentry.replayIntegration({
			maskAllText: true,
			blockAllMedia: true
		})
	],

	// Enable source maps in production
	enabled: import.meta.env.MODE === 'production',

	// Ignore known non-critical errors
	ignoreErrors: [
		// Browser extensions
		'ResizeObserver loop',
		'Non-Error promise rejection',
		// Network errors that don't need tracking
		'NetworkError',
		'Failed to fetch'
	]
});

/**
 * Register hooks to run on the client-side before SvelteKit handles a request
 */
export const handleNavigate = () => {
	// Services are now registered in +layout.svelte at module load time
	if (browser) {
		// Check token on every navigation
		if (isAuthenticated()) {
			const remainingMs = getTokenRemainingTime();
			devLog(`[Navigation] Checking token: ${Math.floor(remainingMs / 60000)} minutes remaining`);

			// If less than 10 minutes remaining, refresh proactively
			if (remainingMs < 10 * 60 * 1000 || shouldRefreshToken()) {
				devLog('[Navigation] Refreshing token during navigation');
				window.dispatchEvent(new CustomEvent('auth:refresh-token-needed'));
			}
		}
	}

	// Continue with navigation
	return;
};

/**
 * Handle client-side errors with Sentry integration
 * This captures unhandled errors and sends them to Sentry
 */
export const handleError: HandleClientError = ({ error, event, status, message }) => {
	// Log error details for debugging
	devLog('[Client Error Handler]', { error, status, message, url: event.url.pathname });

	// Send to Sentry with additional context
	Sentry.captureException(error, {
		contexts: {
			sveltekit: {
				event: event.url.pathname,
				status,
				message
			}
		},
		tags: {
			component: 'client',
			status: status.toString()
		}
	});

	// Return error details to SvelteKit
	return {
		message:
			import.meta.env.MODE === 'production'
				? 'An unexpected error occurred'
				: message || 'Unknown error'
	};
};
